import { getLocalStorageValue } from "@/api/localStorage";
import { GetRequestPublic } from "@/api/RequestTemplates";
import ClientGTM from "react-gtm-module";
import { SentryError } from "@/shared/sentry/sentry";

export const handleGtmInit = () => {
  const GOOGLE_TAG_MANAGER_CONTAINER_ID = process.env.GOOGLE_TAG_MANAGER_CONTAINER_ID;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).gtmInitialized) {
    return;
  }

  if (
    typeof GOOGLE_TAG_MANAGER_CONTAINER_ID !== "undefined" &&
    GOOGLE_TAG_MANAGER_CONTAINER_ID !== null &&
    GOOGLE_TAG_MANAGER_CONTAINER_ID !== ""
  ) {
    GOOGLE_TAG_MANAGER_CONTAINER_ID.split(",").forEach(function (gtm_container) {
      if (typeof gtm_container === "string" && gtm_container.trim() !== "") {
        localStorage.setItem("gtmInitialized", "true");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).gtmInitialized = true;
        ClientGTM.initialize({ gtmId: gtm_container });
      }
    });
  } else {
    console.error("GOOGLE_TAG_MANAGER_CONTAINER_ID is not defined");
  }
};

type WindowWithDataLayer = Window & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export const virtualPageview = (pageUrl: string, pageTitle: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "virtualPageview",
    pageUrl,
    pageTitle,
  });
};

export const genericGtmEvent = (event: string, payload: Record<string, unknown> = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    payload,
  });
};

export const shortOfferFormStepProgression = (
  step?: string,
  secondaryValue?: string,
  clickedStep?: string,
  category?: string,
  page?: string,
  email?: string
) => {
  const payload = filterUndefinedParams({ step, secondaryValue, clickedStep, category, page, email });
  return genericGtmEvent("SOF_STEP_PROGRESSION", payload);
};

const filterUndefinedParams = (params: Record<string, unknown>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined));
};

export const sendRmdTrxEvent = async (
  eventName: string, 
  userId: string, 
  refId: string, 
  rmdTrx?: Record<string, string>
) => {
  try {
    if (!process.env.TRX_API_URL) {
      console.error('TRX_API_URL is not defined in environment variables');
      return;
    }

    // Get RMD tracking params from URL if not provided
    let rmdTrxParams = rmdTrx;
    if (!rmdTrxParams) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const queryParams = Object.fromEntries(urlSearchParams.entries());
      
      rmdTrxParams = {
        rmd_trx_source: queryParams["rmd_trx_source"],
        rmd_trx_content: queryParams["rmd_trx_content"],
        rmd_trx_campaign: queryParams["rmd_trx_campaign"],
        rmd_trx_medium: queryParams["rmd_trx_medium"],
        rmd_trx_term: queryParams["rmd_trx_term"],
      };

      // Filter out undefined values
      rmdTrxParams = Object.fromEntries(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(rmdTrxParams).filter(([_, value]) => value !== undefined)
      );
    }

    // If no RMD params in URL, try localStorage
    if (Object.keys(rmdTrxParams).length === 0) {
      rmdTrxParams = getLocalStorageValue("rmd_trx");
    }

    // Create base payload with required fields and index signature for dynamic properties
    const payload: {
      event: string;
      user_id: string;
      ref_id: string;
      [key: string]: string | undefined;
    } = {
      event: eventName,
      user_id: userId,
      ref_id: refId,
    };
    
    // report in sentry if eventName is undefined
    if (!eventName) {
      SentryError('Event name is undefined', {
        eventName,
        userId,
        refId,
        rmdTrxParams
      });
    }
    

    // Only add RMD params that are defined
    if (rmdTrxParams) {
      Object.entries(rmdTrxParams).forEach(([key, value]) => {
        if (value !== undefined) {
          payload[key] = value;
        }
      });
    }

    const response = await GetRequestPublic(process.env.TRX_API_URL, {
      params: payload
    });

    if (response.status !== 200) {
      console.error('Failed to send RMD transaction event:', response.statusText);
      return;
    }

    // Optionally send to GTM as well
    genericGtmEvent('RMD_TRX', payload);

    return response;
  } catch (error) {
    console.error('Error sending RMD transaction event:', error);
    throw error;
  }
};

